import { zodResolver } from '@hookform/resolvers/zod'
import {
  Captions,
  Eye,
  EyeOff,
  Globe,
  Hash,
  Image as ImageIcon,
  Layers,
  MessageSquare,
  Minus,
  MessageCircle,
  Plus,
  Sparkles,
  Users,
  Wand2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import { SuggestPromptButton } from '@/features/factory/components/SuggestPromptButton'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import {
  useStartRedditStories,
  useSuggestPrompt,
  type RedditStoriesInput,
} from '@/api/endpoints/factories'
import { useThemes, type Theme } from '@/api/endpoints/themes'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { NarratorPicker } from '../_shared/NarratorPicker'
import { PresetBar } from '../_shared/PresetBar'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'

const FIELD_LABELS: Record<string, string> = {
  theme: 'Tema',
  count: 'Quantidade',
  language: 'Idioma',
  num_segments: 'Segmentos',
  custom_prompt: 'Prompt',
  subreddit_override: 'Subreddit',
  stock_source: 'Vídeo de fundo',
  subtitle_position: 'Posição da legenda',
  tone: 'Tom',
  tts_voice_pt: 'Narrador (PT)',
  tts_voice_en: 'Narrador (EN)',
}

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en' | 'both'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

type ToneKey = 'dramatic' | 'deadpan' | 'outraged' | 'amused'
const TONES: ReadonlyArray<SegmentedOption<ToneKey>> = [
  { value: 'dramatic', label: 'Dramático' },
  { value: 'deadpan', label: 'Sério' },
  { value: 'outraged', label: 'Indignado' },
  { value: 'amused', label: 'Divertido' },
]

type StockSourceKey = 'pexels' | 'pixabay'
const STOCK_SOURCES: ReadonlyArray<SegmentedOption<StockSourceKey>> = [
  { value: 'pexels', label: 'Pexels' },
  { value: 'pixabay', label: 'Pixabay' },
]

// Subtitle vertical placement. ``top`` deliberately omitted — the post-header
// card occupies the top zone during the hook, so subs there would collide.
// Default is ``center`` (storytime reads better in the middle, and the
// theme JSONs ship the same default).
type SubtitlePositionKey = 'center' | 'bottom'
const SUBTITLE_POSITIONS: ReadonlyArray<SegmentedOption<SubtitlePositionKey>> = [
  { value: 'center', label: 'Meio' },
  { value: 'bottom', label: 'Embaixo' },
]

type SubredditKey =
  | 'aita'
  | 'tifu'
  | 'malicious_compliance'
  | 'pro_revenge'
  | 'entitled_parents'
  | 'choosing_beggars'
  | 'relationship_advice'

// ``'auto'`` is a sentinel for "no override — pull subreddit from theme slug".
// Radix's <Select.Item> forbids ``value=""`` (it reserves the empty string for
// clearing the selection), so we model the absence of an override as the
// literal string 'auto' and convert it to ``null`` on submit.
const AUTO_SUBREDDIT = 'auto' as const

const SUBREDDIT_OPTIONS: ReadonlyArray<{ value: typeof AUTO_SUBREDDIT | SubredditKey; label: string }> = [
  { value: AUTO_SUBREDDIT, label: 'Auto (vem do tema)' },
  { value: 'aita', label: 'r/AmItheAsshole' },
  { value: 'tifu', label: 'r/tifu' },
  { value: 'malicious_compliance', label: 'r/MaliciousCompliance' },
  { value: 'pro_revenge', label: 'r/ProRevenge' },
  { value: 'entitled_parents', label: 'r/EntitledParents' },
  { value: 'choosing_beggars', label: 'r/ChoosingBeggars' },
  { value: 'relationship_advice', label: 'r/relationships' },
]

const META_THEME_OPTIONS = [{ value: 'custom', label: 'Prompt customizado' }] as const

const schema = z
  .object({
    theme: z.string().min(1, 'Escolha um tema ou prompt'),
    count: z.number().int().min(1).max(10),
    language: z.enum(['pt-br', 'en', 'both']),
    num_segments: z.number().int().min(3).max(8),
    custom_prompt: z.string().trim().optional(),
    tone: z.enum(['dramatic', 'deadpan', 'outraged', 'amused']).optional(),
    stock_source: z.enum(['pexels', 'pixabay']).optional(),
    subtitle_position: z.enum(['center', 'bottom']),
    subreddit_override: z
      .enum([
        AUTO_SUBREDDIT,
        'aita',
        'tifu',
        'malicious_compliance',
        'pro_revenge',
        'entitled_parents',
        'choosing_beggars',
        'relationship_advice',
      ])
      .optional(),
    chat_bubbles_enabled: z.boolean(),
    anonymize_names: z.boolean(),
    tts_voice_pt: z.string().optional(),
    tts_voice_en: z.string().optional(),
    llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
  })
  // Empty custom_prompt is valid — backend auto-generates via suggest_prompt.
  .refine(
    (v) => {
      if (v.theme !== 'custom') return true
      const len = v.custom_prompt?.length ?? 0
      return len === 0 || len >= 10
    },
    {
      path: ['custom_prompt'],
      message: 'Descreva com pelo menos 10 caracteres, ou deixe em branco para gerar um automaticamente.',
    },
  )

type FormValues = z.infer<typeof schema>

const FORM_DEFAULTS: FormValues = {
  theme: 'reddit_stories-aita',
  count: 1,
  language: 'pt-br',
  num_segments: 5,
  custom_prompt: '',
  chat_bubbles_enabled: true,
  anonymize_names: true,
  subreddit_override: AUTO_SUBREDDIT,
  subtitle_position: 'center',
}

function formToPayload(values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    theme: values.theme,
    language: values.language,
    num_segments: values.num_segments,
    chat_bubbles_enabled: values.chat_bubbles_enabled,
    anonymize_names: values.anonymize_names,
    subtitle_position: values.subtitle_position,
    source_mode: 'fabricated',
    custom_prompt: values.theme === 'custom' && values.custom_prompt ? values.custom_prompt : '',
  }
  if (values.subreddit_override && values.subreddit_override !== AUTO_SUBREDDIT) {
    payload.subreddit_override = values.subreddit_override
  }
  if (values.tone) payload.tone = values.tone
  if (values.stock_source) payload.stock_source = values.stock_source
  if (values.tts_voice_pt) payload.tts_voice_pt = values.tts_voice_pt
  if (values.tts_voice_en) payload.tts_voice_en = values.tts_voice_en
  return payload
}

function payloadToForm(params: Record<string, unknown>): FormValues {
  const v: FormValues = {
    ...FORM_DEFAULTS,
    theme: (params.theme as string | undefined) ?? FORM_DEFAULTS.theme,
    language:
      ((params.language as FormValues['language'] | undefined) ?? null) ?? FORM_DEFAULTS.language,
    num_segments: (params.num_segments as number | undefined) ?? FORM_DEFAULTS.num_segments,
    custom_prompt: (params.custom_prompt as string | undefined) ?? '',
    chat_bubbles_enabled:
      (params.chat_bubbles_enabled as boolean | undefined) ?? FORM_DEFAULTS.chat_bubbles_enabled,
    anonymize_names:
      (params.anonymize_names as boolean | undefined) ?? FORM_DEFAULTS.anonymize_names,
    subreddit_override:
      (params.subreddit_override as FormValues['subreddit_override'] | undefined) ??
      AUTO_SUBREDDIT,
    subtitle_position:
      (params.subtitle_position as FormValues['subtitle_position'] | undefined) ??
      FORM_DEFAULTS.subtitle_position,
  }
  if (params.tone) v.tone = params.tone as FormValues['tone']
  if (params.stock_source === 'pexels' || params.stock_source === 'pixabay') {
    v.stock_source = params.stock_source
  }
  if (params.tts_voice_pt) v.tts_voice_pt = params.tts_voice_pt as string
  if (params.tts_voice_en) v.tts_voice_en = params.tts_voice_en as string
  return v
}

export function RedditStoriesForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const { data: allThemes = [], isPending: themesPending } = useThemes()
  // Only reddit_stories-* themes apply here. The themes endpoint returns
  // every JSON in config/themes/, so filter client-side by slug prefix.
  const themes = allThemes.filter((t: Theme) => t.slug.startsWith('reddit_stories-'))

  const startMutation = useStartRedditStories()
  const suggestPrompt = useSuggestPrompt()

  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      theme: themes[0]?.slug ?? 'reddit_stories-aita',
      count: 1,
      language: 'pt-br',
      num_segments: 5,
      custom_prompt: '',
      chat_bubbles_enabled: true,
      anonymize_names: true,
      subreddit_override: AUTO_SUBREDDIT,
      // Reddit storytime reads better with subs in the middle (the post-header
      // card occupies the top zone, the bottom is where Shorts overlays the
      // creator's avatar). User can flip to ``bottom`` from Ajuste fino.
      subtitle_position: 'center',
      llm_provider: 'auto',
    },
  })

  const theme = form.watch('theme')
  const count = form.watch('count')
  const language = form.watch('language')
  const numSegments = form.watch('num_segments')
  const tone = form.watch('tone')
  const stockSource = form.watch('stock_source')
  const subtitlePosition = form.watch('subtitle_position')
  const subredditOverride = form.watch('subreddit_override')
  const chatBubblesEnabled = form.watch('chat_bubbles_enabled')
  const anonymizeNames = form.watch('anonymize_names')
  const voicePt = form.watch('tts_voice_pt')
  const voiceEn = form.watch('tts_voice_en')
  const llmProvider = form.watch('llm_provider')

  const onSubmit = form.handleSubmit((values) => {
    const payload: RedditStoriesInput = {
      theme: values.theme,
      channel: channel || null,
      count: values.count,
      language: values.language,
      num_segments: values.num_segments,
      // v1: only fabricated mode is wired in the backend; the UI hides the
      // toggle. Surfacing it once `inspired` ships in v2 is a single line.
      source_mode: 'fabricated',
      subreddit_override:
        values.subreddit_override && values.subreddit_override !== AUTO_SUBREDDIT
          ? values.subreddit_override
          : null,
      chat_bubbles_enabled: values.chat_bubbles_enabled,
      anonymize_names: values.anonymize_names,
      stock_source: values.stock_source ?? null,
      subtitle_position: values.subtitle_position,
      force: false,
      custom_prompt: values.custom_prompt ?? '',
      tone: values.tone ?? null,
      tts_voice_pt: values.tts_voice_pt || null,
      tts_voice_en: values.tts_voice_en || null,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <MessageCircle aria-hidden /> Reddit Stories · storytime narrado
          </div>
          <h1 className={styles.title}>Que história vamos contar?</h1>
          <p className={styles.subtitle}>
            Escolha o subreddit; o Prism inventa uma história autêntica em primeira pessoa, com vozes alternadas para os
            diálogos diretos.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<FormValues>
              factory="reddit_stories"
              channel={channel}
              formValues={form.watch()}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={(values) => {
                form.reset(values)
              }}
            />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<Hash />} label="Tema" htmlFor="reddit-theme">
              <SelectRoot
                value={theme}
                onValueChange={(v) => {
                  form.setValue('theme', v, { shouldValidate: true })
                }}
              >
                <SelectTrigger id="reddit-theme" aria-label="Tema">
                  <SelectValue placeholder={themesPending ? 'Carregando...' : 'Escolha um tema'} />
                </SelectTrigger>
                <SelectContent>
                  {META_THEME_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                  {themes.map((t: Theme) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
              {form.formState.errors.theme ? (
                <span className={styles.errorText}>{form.formState.errors.theme.message}</span>
              ) : null}
            </Row>

            {theme === 'custom' ? (
              <Row icon={<Wand2 />} label="Prompt" htmlFor="reddit-prompt">
                <textarea
                  id="reddit-prompt"
                  className={cn(
                    styles.textarea,
                    form.formState.errors.custom_prompt && styles.textareaError,
                  )}
                  placeholder="Ex: r/MaliciousCompliance — chefe pediu para seguir manual à risca"
                  rows={3}
                  {...form.register('custom_prompt')}
                />
                <SuggestPromptButton
                  factory="reddit_stories"
                  language={language}
                  channel={channel}
                  pending={suggestPrompt.isPending}
                  error={suggestPrompt.error}
                  onClick={() => {
                    suggestPrompt.mutate(
                      { factory: 'reddit_stories', language, channel: channel || null },
                      {
                        onSuccess: (prompt) => {
                          form.setValue('custom_prompt', prompt, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        },
                      },
                    )
                  }}
                />
                {form.formState.errors.custom_prompt ? (
                  <span className={styles.errorText}>{form.formState.errors.custom_prompt.message}</span>
                ) : null}
              </Row>
            ) : null}

            <Row icon={<Layers />} label="Segmentos">
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={numSegments <= 3}
                  onClick={() => {
                    form.setValue('num_segments', Math.max(3, numSegments - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{numSegments}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={numSegments >= 8}
                  onClick={() => {
                    form.setValue('num_segments', Math.min(8, numSegments + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            <Row icon={<Layers />} label="Quantidade">
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={count <= 1}
                  onClick={() => {
                    form.setValue('count', Math.max(1, count - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{count}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={count >= 10}
                  onClick={() => {
                    form.setValue('count', Math.min(10, count + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            {advancedOpen ? (
              <div className={styles.advanced}>
                <AdvSection icon={<Globe />} label="Idioma">
                  <Segmented<'pt-br' | 'en' | 'both'>
                    options={LANGUAGES}
                    value={language}
                    onValueChange={(v) => {
                      form.setValue('language', v)
                    }}
                    aria-label="Idioma"
                  />
                </AdvSection>

                <AdvSection icon={<Users />} label="Subreddit (override)" htmlFor="reddit-sub">
                  <SelectRoot
                    value={subredditOverride ?? AUTO_SUBREDDIT}
                    onValueChange={(v) => {
                      form.setValue(
                        'subreddit_override',
                        v as typeof AUTO_SUBREDDIT | SubredditKey,
                      )
                    }}
                  >
                    <SelectTrigger id="reddit-sub" aria-label="Subreddit override">
                      <SelectValue placeholder="Auto (vem do tema)" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBREDDIT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </SelectRoot>
                </AdvSection>

                <AdvSection icon={<ImageIcon />} label="Vídeo de fundo">
                  <Segmented<StockSourceKey>
                    options={STOCK_SOURCES}
                    value={stockSource ?? ('pexels')}
                    onValueChange={(v) => {
                      form.setValue('stock_source', v)
                    }}
                    aria-label="Stock source"
                  />
                </AdvSection>

                <AdvSection icon={<Captions />} label="Posição da legenda">
                  <Segmented<SubtitlePositionKey>
                    options={SUBTITLE_POSITIONS}
                    value={subtitlePosition}
                    onValueChange={(v) => {
                      form.setValue('subtitle_position', v)
                    }}
                    aria-label="Posição da legenda"
                  />
                </AdvSection>

                <AdvSection icon={<MessageSquare />} label="Tom">
                  <Segmented<ToneKey>
                    options={TONES}
                    value={tone ?? ('dramatic')}
                    onValueChange={(v) => {
                      form.setValue('tone', v)
                    }}
                    aria-label="Tom"
                  />
                </AdvSection>

                <AdvSection icon={chatBubblesEnabled ? <MessageCircle /> : <MessageCircle />} label="Bubbles de quote">
                  <Segmented<'on' | 'off'>
                    options={[
                      { value: 'on', label: 'Ativado' },
                      { value: 'off', label: 'Desligado' },
                    ]}
                    value={chatBubblesEnabled ? 'on' : 'off'}
                    onValueChange={(v) => {
                      form.setValue('chat_bubbles_enabled', v === 'on')
                    }}
                    aria-label="Bubbles de quote"
                  />
                </AdvSection>

                <AdvSection icon={anonymizeNames ? <EyeOff /> : <Eye />} label="Anonimização">
                  <Segmented<'on' | 'off'>
                    options={[
                      { value: 'on', label: 'Ativada' },
                      { value: 'off', label: 'Desligada' },
                    ]}
                    value={anonymizeNames ? 'on' : 'off'}
                    onValueChange={(v) => {
                      form.setValue('anonymize_names', v === 'on')
                    }}
                    aria-label="Anonimização"
                  />
                </AdvSection>

                <NarratorPicker
                  language={language}
                  voicePt={voicePt ?? ''}
                  voiceEn={voiceEn ?? ''}
                  onVoicePtChange={(v) => {
                    form.setValue('tts_voice_pt', v)
                  }}
                  onVoiceEnChange={(v) => {
                    form.setValue('tts_voice_en', v)
                  }}
                />

                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="reddit_stories" />
            <button type="submit" className={styles.generateBtn} disabled={!channel || startMutation.isPending}>
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function AdvSection({ icon, label, htmlFor, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      {htmlFor ? (
        <label className={styles.advSectionLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.advSectionLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      {children}
    </section>
  )
}

